import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from '../components/Logo';
import {Link} from 'react-router-dom';

const {
  FiArrowLeft,
  FiExternalLink,
  FiShieldOff,
  FiEyeOff,
  FiLock,
  FiAlertTriangle,
  FiUsers,
  FiFileText,
  FiBarChart2,
  FiEye,
  FiGavel,
  FiInfo,
  FiMail,
  FiPhone,
  FiCheck,
  FiXCircle,
  FiType,
  FiMove,
  FiCpu
} = FiIcons;

function RegulatoryPage() {
  const complaintData = [
    ['1', 'Directly from Investors', '0', '0', '0', '0', '-'],
    ['2', 'SEBI (SCORES)', '0', '0', '0', '0', '-'],
    ['3', 'Other Sources (if any)', '0', '0', '0', '0', '-'],
    ['4', 'Grand Total', '0', '0', '0', '0', '-']
  ];

  const disclosureLinks = [
    ['Privacy Policy', 'How we collect and protect your data', FiShieldOff, 'privacy'],
    ['Terms & Conditions', 'Detailed terms governing our services', FiFileText, 'terms'],
    ['Investor Charter', 'Rights and responsibilities of investors', FiUsers, 'charter'],
    ['Disclosure Document', 'SEBI mandated disclosure information', FiFileText, 'disclosure'],
    ['Complaint Data', 'Monthly disclosure of investor complaints', FiBarChart2, 'complaints'],
    ['Accessibility', 'BSE Accessibility Compliance (WCAG 2.1)', FiEye, 'accessibility'],
    ['Investor Grievances', 'Complaint redressal procedures', FiGavel, 'grievances']
  ];

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-gray-950 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center text-emerald-400 hover:text-emerald-300 font-black uppercase text-[10px] tracking-widest mb-8"
          >
            <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" aria-hidden="true" />
            Back to Home
          </Link>
          <Logo dark className="mb-8" />
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter">
            Risk & Disclosures
          </h1>
          <p className="text-gray-300 mt-4 font-bold uppercase text-xs tracking-[0.3em]">
            Mandatory regulatory documentation for WealthGen
          </p>
        </div>
      </header>

      <main id="main-content" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-amber-50 border border-amber-200 p-6 rounded-xl mb-12 flex items-start">
          <SafeIcon icon={FiAlertTriangle} className="h-6 w-6 text-amber-600 mr-4 mt-1" aria-hidden="true" />
          <p className="text-amber-800 text-sm font-medium leading-relaxed">
            Investments in securities market are subject to market risks. Read all related
            documents carefully before investing. SEBI RA: INH000023436.
          </p>
        </div>

        <nav
          aria-label="Regulatory document sections"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16"
        >
          {disclosureLinks.map(([title, description, icon, section]) => (
            <a
              key={section}
              href={`#${section}`}
              className="group flex min-h-[150px] flex-col items-start justify-start rounded-2xl border border-gray-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-1 hover:border-emerald-500 hover:bg-emerald-50 hover:shadow-md focus-visible:bg-emerald-50 focus-visible:outline focus-visible:outline-3 focus-visible:outline-emerald-700 focus-visible:outline-offset-2"
            >
              <SafeIcon
                icon={icon}
                className="mb-4 h-6 w-6 text-emerald-700 transition-transform group-hover:scale-110"
                aria-hidden="true"
              />
              <span className="text-sm font-black uppercase tracking-wide text-gray-900">
                {title}
              </span>
              <span className="mt-2 text-xs leading-relaxed text-gray-600">
                {description}
              </span>
            </a>
          ))}
        </nav>

        <section id="privacy" className="py-12 mb-8 scroll-mt-24">
          <h2 className="text-xs font-black text-emerald-700 mb-8 uppercase tracking-[0.4em]">Privacy Policy</h2>
          <div className="bg-emerald-50 p-8 rounded-3xl border border-emerald-100 mb-8">
            <div className="flex items-center mb-6">
              <SafeIcon icon={FiShieldOff} className="h-8 w-8 text-emerald-600 mr-4" aria-hidden="true" />
              <h3 className="text-2xl font-black text-emerald-900">Strict Privacy Commitment</h3>
            </div>
            <p className="text-emerald-800 text-lg leading-relaxed">
              Makarand Godbole (WealthGen) operates this website as a purely informational platform.
              <strong> We do not collect, store, or process any personal information</strong> from
              visitors. There are no sign-up forms, tracking cookies, or databases used to identify you.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100">
              <SafeIcon icon={FiEyeOff} className="h-6 w-6 text-gray-500 mb-4" aria-hidden="true" />
              <h3 className="font-black text-gray-900 uppercase text-xs tracking-widest mb-2">No Tracking</h3>
              <p className="text-gray-600 text-sm leading-relaxed">We do not use tracking scripts that monitor your behavior across the web.</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100">
              <SafeIcon icon={FiLock} className="h-6 w-6 text-gray-500 mb-4" aria-hidden="true" />
              <h3 className="font-black text-gray-900 uppercase text-xs tracking-widest mb-2">No Data Storage</h3>
              <p className="text-gray-600 text-sm leading-relaxed">No personal identifiers are stored or sold. Your visit is entirely anonymous.</p>
            </div>
          </div>
        </section>

        <section id="terms" className="py-12 mb-8 scroll-mt-24">
          <h2 className="text-xs font-black text-emerald-700 mb-8 uppercase tracking-[0.4em]">Terms & Conditions</h2>
          <div className="bg-amber-50 p-8 rounded-3xl border border-amber-100 mb-8">
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiAlertTriangle} className="h-6 w-6 text-amber-600 mr-3" aria-hidden="true" />
              <h3 className="text-xl font-black text-amber-900 uppercase tracking-tight">Non-Commercial Platform</h3>
            </div>
            <p className="text-amber-800">
              This website is strictly for informational and regulatory compliance.
              <strong> We do not sell any courses, subscriptions, or financial products.</strong>
              No commercial transactions are conducted here.
            </p>
          </div>
          <div className="space-y-8 text-gray-600 leading-relaxed">
            <div>
              <h3 className="text-lg font-black text-gray-900 mb-3">1. Informational Content</h3>
              <p>All material provided is for educational purposes only and does not constitute a solicitation to buy or sell securities.</p>
            </div>
            <div>
              <h3 className="text-lg font-black text-gray-900 mb-3">2. Intellectual Property</h3>
              <p>The professional insights and design elements belong to Makarand Godbole. Personal, non-commercial use is permitted.</p>
            </div>
          </div>
        </section>

        <section id="charter" className="py-12 mb-8 scroll-mt-24">
          <h2 className="text-xs font-black text-emerald-700 mb-8 uppercase tracking-[0.4em]">Investor Charter</h2>
          <div className="space-y-10 text-gray-700 leading-relaxed">
            <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100">
              <h3 className="text-lg font-black text-gray-900 mb-4">Vision Statement</h3>
              <p className="italic">"To facilitate investors to take informed investment decisions based on objective and high-quality research."</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-3xl border border-gray-100">
              <h3 className="text-lg font-black text-gray-900 mb-4">Mission Statement</h3>
              <p>"To provide objective, independent and unbiased research that helps investors analyze and monitor their investments effectively."</p>
            </div>
            <div>
              <h3 className="text-lg font-black text-gray-900 mb-4">Rights of Investors</h3>
              <ul className="list-disc pl-6 space-y-3">
                <li>Get high-quality research and recommendations based on independent analysis.</li>
                <li>Receive all disclosures from the RA as per regulations.</li>
                <li>Have a process for grievance redressal.</li>
                <li>Be informed about the RA's registration status and pending disciplinary actions.</li>
              </ul>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-emerald-50 p-8 rounded-3xl border border-emerald-100">
                <h3 className="text-sm font-black text-emerald-900 mb-6 uppercase tracking-widest flex items-center">
                  <SafeIcon icon={FiCheck} className="mr-2 h-5 w-5" aria-hidden="true" /> DOs
                </h3>
                <ul className="space-y-3 text-sm text-emerald-900">
                  <li>Always deal with SEBI registered Research Analysts.</li>
                  <li>Verify the RA's registration on the SEBI website.</li>
                  <li>Read all disclosure documents carefully.</li>
                  <li>Pay attention to specific risks mentioned in research reports.</li>
                </ul>
              </div>
              <div className="bg-red-50 p-8 rounded-3xl border border-red-100">
                <h3 className="text-sm font-black text-red-900 mb-6 uppercase tracking-widest flex items-center">
                  <SafeIcon icon={FiXCircle} className="mr-2 h-5 w-5" aria-hidden="true" /> DON'Ts
                </h3>
                <ul className="space-y-3 text-sm text-red-900">
                  <li>Do not deal with unregistered entities.</li>
                  <li>Do not fall for guaranteed returns or assured profits.</li>
                  <li>Do not share personal trading credentials.</li>
                  <li>Do not invest based on rumors or unauthorized tips.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="disclosure" className="py-12 mb-8 scroll-mt-24">
          <h2 className="text-xs font-black text-emerald-700 mb-8 uppercase tracking-[0.4em]">RA Disclosure Document</h2>
          <div className="bg-emerald-50 p-10 rounded-3xl border border-emerald-100">
            <h3 className="text-2xl font-black text-gray-900 mb-4">Makarand Godbole (WealthGen)</h3>
            <p className="text-emerald-800 font-bold mb-6">SEBI Registration No: INH000023436</p>
            <p className="text-gray-700 leading-relaxed">This document provides essential information about the Research and Recommendation services to enable prospective clients to make an informed decision.</p>
          </div>
          <div className="mt-10 space-y-8 text-gray-700 leading-relaxed">
            <div>
              <h3 className="text-lg font-black text-gray-900 mb-3">Background & Present Business</h3>
              <p>Registered as a Research Analyst since 2025. Primary focus is structural analysis and governance-led research of financial instruments.</p>
            </div>
            <div>
              <h3 className="text-lg font-black text-gray-900 mb-3">Disciplinary History</h3>
              <p>There are no pending material litigations or legal proceedings against the Research Analyst by any regulatory authority.</p>
            </div>
          </div>
        </section>

        <section id="complaints" className="py-12 mb-8 scroll-mt-24">
          <h2 className="text-xs font-black text-emerald-700 mb-8 uppercase tracking-[0.4em]">Investor Complaints Data</h2>
          <div className="bg-emerald-50 p-6 rounded-2xl border border-emerald-100 mb-10 flex items-start">
            <SafeIcon icon={FiInfo} className="h-5 w-5 text-emerald-700 mr-4 mt-1" aria-hidden="true" />
            <p className="text-emerald-900 text-sm font-medium leading-relaxed">
              In compliance with SEBI circular SEBI/HO/MIRSD/MIRSD_CRMD/P/CIR/2021/676, the following data is disclosed regarding complaints received against Makarand Godbole (WealthGen). <strong>We have a zero-complaint record.</strong>
            </p>
          </div>

          <div
            tabIndex={0}
            role="region"
            aria-label="Investor complaints data table"
            className="overflow-x-auto rounded focus-visible:outline focus-visible:outline-3 focus-visible:outline-emerald-700 focus-visible:outline-offset-4"
          >
            <table className="compliance-table">
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Received From</th>
                  <th>Pending at Start</th>
                  <th>Received</th>
                  <th>Resolved*</th>
                  <th>Pending**</th>
                  <th>Avg. Resolution Time^</th>
                </tr>
              </thead>
              <tbody>
                {complaintData.map((row) => (
                  <tr key={row[0]} className={row[0] === '4' ? 'bg-gray-50 font-black' : ''}>
                    {row.map((cell, index) => <td key={index}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section id="accessibility" className="py-12 mb-8 scroll-mt-24">
          <h2 className="text-xs font-black text-emerald-700 mb-8 uppercase tracking-[0.4em]">Accessibility Compliance</h2>
          <p className="text-gray-700 text-2xl leading-relaxed mb-12">Makarand Godbole (WealthGen) is committed to digital inclusivity. This platform is built to align with <strong>WCAG 2.1 Level AA</strong>.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              ['Perceivable Information', FiType, 'Text alternatives, appropriate color contrast, and adaptable layouts.'],
              ['Operable Interface', FiMove, 'Keyboard accessibility, skip navigation, and visible focus indicators.'],
              ['Understandable & Robust', FiCpu, 'Semantic HTML, predictable navigation, and ARIA compatibility.']
            ].map(([title, icon, description]) => (
              <div key={title} className="bg-gray-50 p-8 rounded-[32px] border border-gray-100">
                <SafeIcon icon={icon} className="h-6 w-6 text-emerald-700 mb-6" aria-hidden="true" />
                <h3 className="text-lg font-black text-gray-900 mb-4">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="grievances" className="py-12 scroll-mt-24">
          <h2 className="text-xs font-black text-emerald-700 mb-8 uppercase tracking-[0.4em]">Investor Grievances</h2>
          <div className="bg-amber-50 border border-amber-200 p-8 rounded-3xl flex items-start mb-10">
            <SafeIcon icon={FiInfo} className="h-6 w-6 text-amber-700 mt-1 mr-4" aria-hidden="true" />
            <div>
              <h3 className="text-xl font-black text-amber-900 mb-2">Escalation Matrix</h3>
              <p className="text-amber-800">Investors are encouraged to resolve grievances directly with the Research Analyst before escalating to regulatory bodies.</p>
            </div>
          </div>
          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm space-y-6 mb-10">
            <div className="flex items-center">
              <SafeIcon icon={FiMail} className="h-5 w-5 text-emerald-700 mr-4" aria-hidden="true" />
              <span className="text-gray-900 font-bold">ramak.godbole@gmail.com</span>
            </div>
            <div className="flex items-center">
              <SafeIcon icon={FiPhone} className="h-5 w-5 text-emerald-700 mr-4" aria-hidden="true" />
              <span className="text-gray-900 font-bold">+91 89993 49416</span>
            </div>
          </div>
          <p className="text-gray-600 mb-8">If your grievance is not resolved within 30 days, you may escalate it through the official SEBI SCORES or SMART ODR platforms.</p>
          <div className="flex flex-wrap gap-4">
            <a href="https://scores.sebi.gov.in/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-blue-700 text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-blue-800 focus-visible:bg-blue-800 transition-all">
              Open SEBI SCORES
              <SafeIcon icon={FiExternalLink} className="ml-2 h-4 w-4" aria-hidden="true" />
            </a>
            <a href="https://smartodr.in/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-gray-900 text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-emerald-700 focus-visible:bg-emerald-700 transition-all">
              Open SMART ODR
              <SafeIcon icon={FiExternalLink} className="ml-2 h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}

export default RegulatoryPage;